import type { Room } from '~/shared/types/payload-types'
import { ManyRoomsQuery } from '~/queries/many/rooms'

type RoomsEntry = {
  docs: Room[]
  loaded: boolean
  pending: boolean
  error: unknown
}

const emptyEntry = (): RoomsEntry => ({ docs: [], loaded: false, pending: false, error: null })

// in-flight requests per cache key, so the plugin preload and a page mounting at the
// same moment share one request instead of firing two
const inFlight = new Map<string, Promise<Room[]>>()

// works both inside setup (pages/components) and inside plugins, where useI18n() is unavailable
export const useCurrentLocale = () => {
  const i18n = useNuxtApp().$i18n as { locale?: Ref<string> } | undefined
  return computed(() => i18n?.locale?.value ?? 'nl') // 'nl' = defaultLocale in nuxt.config
}

/**
 * All rooms, fetched once per locale and kept in useState for the rest of the visit.
 * Preloaded on idle by plugins/preload-rooms.client.ts, so /rooms and /rooms/[slug]
 * usually render from cache instead of waiting on a request.
 */
export const useRoomsData = (options: { isPreview?: MaybeRefOrGetter<boolean> } = {}) => {
  const locale = useCurrentLocale()
  const cache = useState<Record<string, RoomsEntry>>('rooms-data', () => ({}))

  const isPreview = computed(() => toValue(options.isPreview) ?? false)
  const cacheKey = computed(() => `${locale.value}:${isPreview.value ? 'preview' : 'published'}`)
  const entry = computed(() => cache.value[cacheKey.value] ?? emptyEntry())

  const load = async ({ force = false }: { force?: boolean } = {}): Promise<Room[]> => {
    const key = cacheKey.value
    const preview = isPreview.value
    const localeValue = locale.value

    if (!force && cache.value[key]?.loaded) return cache.value[key]!.docs

    const existing = inFlight.get(key)
    if (existing) return existing

    cache.value[key] = { ...(cache.value[key] ?? emptyEntry()), pending: true, error: null }

    const request = $fetch<{ docs: Room[]; totalDocs: number }>('/api/payload-gql', {
      method: 'POST',
      body: {
        query: ManyRoomsQuery,
        variables: {
          locale: localeValue,
          isPreview: preview,
          statuses: preview ? ['draft', 'published'] : ['published'],
        },
        isPreview: preview,
        // published rooms come from the server cache (invalidated by the Payload webhook);
        // preview always needs the live draft state
        skipCache: preview,
      },
    })
      .then((result) => {
        const docs = result?.docs ?? []
        cache.value[key] = { docs, loaded: true, pending: false, error: null }
        return docs
      })
      .catch((error) => {
        cache.value[key] = { ...emptyEntry(), error }
        return [] as Room[]
      })
      .finally(() => {
        inFlight.delete(key)
      })

    inFlight.set(key, request)
    return request
  }

  return {
    rooms: computed(() => entry.value.docs),
    loaded: computed(() => entry.value.loaded),
    pending: computed(() => entry.value.pending),
    error: computed(() => entry.value.error),
    load,
  }
}
